import { Config, Connector, getConnectorClient } from '@wagmi/core';
import {
  BrowserProvider,
  JsonRpcSigner,
  TransactionRequest,
  TransactionResponse,
} from 'ethers';
import type { Account, Chain, Client, Transport } from 'viem';

// This is needed due to a bug with current ethers and metamask versions causing a delay until the transaction is executed
// See https://github.com/ethers-io/ethers.js/issues/4722#issuecomment-2100719820
export class UncheckedJsonRpcSigner extends JsonRpcSigner {
  async sendTransaction(
    transaction: TransactionRequest,
  ): Promise<TransactionResponse> {
    return this.sendUncheckedTransaction(transaction).then(
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (hash) => <TransactionResponse>(<unknown>{
          hash,
          nonce: null,
          gasLimit: null,
          gasPrice: null,
          data: null,
          value: null,
          chainId: null,
          confirmations: 0,
          from: null,
          wait: (confirmations?: number) =>
            this.provider.waitForTransaction(hash, confirmations),
        }),
    );
  }
}

export function clientToSigner(
  client: Client<Transport, Chain, Account>,
): JsonRpcSigner {
  const { account, chain, transport } = client;
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };
  const provider = new BrowserProvider(transport, network);
  const signer = new UncheckedJsonRpcSigner(provider, account.address);
  return signer;
}

/** Action to convert a viem Wallet Client to an ethers.js Signer. */
export async function getEthersSigner(
  config: Config,
  { chainId, connector }: { chainId?: number; connector?: Connector } = {},
): Promise<JsonRpcSigner | undefined> {
  const client = await getConnectorClient(config, { chainId, connector });
  return clientToSigner(client);
}
